import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { TypedMemo } from '../../../common/types';
import { closeSMLUpgradePrompt, openSMLUpgradePrompt } from '../../../../redux/slices/appInfo/slice';
import { useAccountId, useSMLUpgradePrompt } from '../../../../redux/slices/appInfo/hooks';
import { SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { CreditPacksModal } from '../../promote/credits/CreditPacksModal';
import { SMLUpgradeModal } from './SMLUpgradeModal';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';

/**
 * Every `M24_LIMIT_*` error, from any client, lands here (see `parseM24LimitError`). A refusal for
 * video credits (402 `M24_LIMIT_QUOTA`, kind `videoCredit`) opens the credit packs on a paid plan
 * (design release-kit g8, n7); its Upgrade button opens the plan prompt. Free and the trial get
 * the plan prompt at once: packs are for paid plans (pricing spec 2026-10-08 §5).
 */
const GlobalSMLUpgradeModalComponent: React.FC = () => {
  const dispatch = useDispatch();
  const prompt = useSMLUpgradePrompt();
  const accountId = useAccountId();
  const capabilities = useMusic24Capabilities();
  const { plan } = capabilities;
  const isPaidPlan = plan !== 'free' && plan !== 'trial';
  const close = useCallback(() => dispatch(closeSMLUpgradePrompt()), [dispatch]);
  const upgrade = useCallback(
    () => dispatch(openSMLUpgradePrompt({ feature: SMLUpgradeFeature.videoCredits })),
    [dispatch]
  );

  if (prompt === null) {
    return null;
  }

  if (prompt.kind === 'videoCredit' && accountId !== null && isPaidPlan) {
    return <CreditPacksModal isOpen accountId={accountId} onClose={close} onUpgrade={upgrade} />;
  }

  return (
    <SMLUpgradeModal
      isOpen
      onClose={close}
      feature={prompt.feature}
      limit={prompt.limit}
      used={prompt.used}
      kind={prompt.kind}
      resetsAt={prompt.resetsAt}
      subject={prompt.subject ?? undefined}
      upsellSurface={prompt.upsellSurface ?? undefined}
    />
  );
};

export const GlobalSMLUpgradeModal = TypedMemo(GlobalSMLUpgradeModalComponent);
